import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { resetPassword as resetPasswordApi } from "../../api/authApi";
import Loader from "../common/Loader";

const base64UrlDecodeUtf8 = (input) => {
  const b64 = input.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
  const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const data = useParams();
  const { token, email } = JSON.parse(base64UrlDecodeUtf8(data.token));

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [tokenValid, setTokenValid] = useState(true);

  // Password strength indicators
  const [strength, setStrength] = useState({
    length: false,
    upper: false,
    lower: false,
    number: false,
    special: false,
  });

  useEffect(() => {
    if (!token || !email) {
      setTokenValid(false);
    }
  }, [token, email]);

  useEffect(() => {
    setStrength({
      length: password.length >= 9,
      upper: /[A-Z]/.test(password),
      lower: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  }, [password]);

  const strengthScore = Object.values(strength).filter(Boolean).length;
  const strengthLabel = [
    "Very Weak",
    "Weak",
    "Fair",
    "Good",
    "Strong",
    "Strong",
  ][strengthScore];
  const strengthColor = [
    "danger",
    "danger",
    "peach-glow",
    "light-orange ",
    "blaze-orange",
    "blaze-orange",
  ][strengthScore];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (strengthScore < 3) {
      setError("Please choose a stronger password.");
      return;
    }

    setLoading(true);

    const result = await resetPasswordApi({
      token,
      confirmPassword,
    });

    if (result.success) {
      setMessage(result.message);
      setTimeout(() => navigate("/login"), 2500);
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  if (!tokenValid) {
    return (
      <div className="container d-flex align-items-center justify-content-center min-vh-100">
        <div
          className="card card-shadow p-4 p-md-5 text-center"
          style={{ maxWidth: "420px", width: "100%" }}
        >
          <i
            className="bi bi-exclamation-triangle-fill text-warning"
            style={{ fontSize: "3rem" }}
          ></i>
          <h4 className="mt-3">Invalid Reset Link</h4>
          <p className="text-theme-muted small">
            This password reset link is invalid or has expired. Please request a
            new one.
          </p>
          <Link to="/forgot-password" className="btn btn-primary mt-2">
            Request New Link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container d-flex align-items-center justify-content-center min-vh-100">
      <div
        className="card card-shadow p-4 p-md-5"
        style={{ maxWidth: "420px", width: "100%" }}
      >
        <div className="text-center mb-4">
          <img
            src="/logo/Paynuts-logo-2026.png"
            alt="PayNuts"
            className="me-2"
            style={{ height: "40px" }}
          />
          <h4 className="ms-mb-2 text-blaze-orange">Reset Password</h4>
          <p className="text-theme-muted small">
            Enter a new password for <strong>{email}</strong>
          </p>
        </div>

        {loading ? (
          <Loader message="Resetting password..." size="medium" />
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="password" className="form-label">
                New Password
              </label>
              <div className="input-group">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  className="form-control"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  autoFocus
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <i className={`bi bi-eye${showPassword ? "-slash" : ""}`}></i>
                </button>
              </div>

              {password && (
                <div className="mt-2">
                  <div className="progress" style={{ height: "5px" }}>
                    <div
                      className={`progress-bar bg-${strengthColor}`}
                      style={{ width: `${(strengthScore / 5) * 100}%` }}
                    ></div>
                  </div>
                  <small className={`text-${strengthColor}`}>
                    {strengthLabel}
                  </small>
                </div>
              )}
            </div>

            <div className="mb-3">
              <label htmlFor="confirmPassword" className="form-label">
                Confirm New Password
              </label>
              <input
                type={showPassword ? "text" : "password"}
                id="confirmPassword"
                className="form-control"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                required
              />
              {confirmPassword && password !== confirmPassword && (
                <small className="text-danger">Passwords do not match.</small>
              )}
            </div>

            <ul className="list-unstyled small mb-3">
              <li
                className={
                  strength.length ? "text-blaze-orange" : "text-theme-muted"
                }
              >
                <i
                  className={`bi ${strength.length ? "bi-check-circle-fill" : "bi-circle"} me-2`}
                ></i>
                At least 9 characters
              </li>
              <li
                className={
                  strength.upper ? "text-blaze-orange" : "text-theme-muted"
                }
              >
                <i
                  className={`bi ${strength.upper ? "bi-check-circle-fill" : "bi-circle"} me-2`}
                ></i>
                One uppercase letter
              </li>
              <li
                className={
                  strength.lower ? "text-blaze-orange" : "text-theme-muted"
                }
              >
                <i
                  className={`bi ${strength.lower ? "bi-check-circle-fill" : "bi-circle"} me-2`}
                ></i>
                One lowercase letter
              </li>
              <li
                className={
                  strength.number ? "text-blaze-orange" : "text-theme-muted"
                }
              >
                <i
                  className={`bi ${strength.number ? "bi-check-circle-fill" : "bi-circle"} me-2`}
                ></i>
                One number
              </li>
              <li
                className={
                  strength.special ? "text-blaze-orange" : "text-theme-muted"
                }
              >
                <i
                  className={`bi ${strength.special ? "bi-check-circle-fill" : "bi-circle"} me-2`}
                ></i>
                One special character
              </li>
            </ul>

            {message && (
              <div className="alert alert-success py-2">{message}</div>
            )}
            {error && <div className="alert alert-danger py-2">{error}</div>}

            <button
              type="submit"
              className="btn btn-primary w-100"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Resetting...
                </>
              ) : (
                "Reset Password"
              )}
            </button>
          </form>
        )}

        <div className="mt-3 text-center">
          <Link to="/login" className="text-decoration-none">
            <i className="bi bi-arrow-left me-1"></i>Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
